// The route-level error boundary: a page that throws must not unmount the whole app.
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'

/** One boundary around the routed page (the same pattern the retired Reports and Insights pages used): a page that throws shows
 *  a message and a reload button inside the layout instead of unmounting the whole app. Cleared on navigation. */
export class PageErrorBoundary extends Component<{ path: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page error:', error, info)
  }
  componentDidUpdate(prev: { path: string }) {
    if (this.state.failed && prev.path !== this.props.path) this.setState({ failed: false })
  }
  render() {
    if (!this.state.failed) return this.props.children
    return (
      <div className="flex flex-col items-center gap-3 p-10 text-center" role="alert" data-testid="page-error">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <p className="font-medium">Something went wrong on this page</p>
        <p className="text-sm text-muted-foreground">Reload to try again, or pick another page from the menu.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Reload
        </Button>
      </div>
    )
  }
}

export function RoutedPage({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return <PageErrorBoundary path={pathname}>{children}</PageErrorBoundary>
}
